import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, ArrowRight, BookOpen } from 'lucide-react';
import { useSession } from '@supabase/auth-helpers-react';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { getRelatedLinks } from '../constants/relatedLinks';

// Extra sections at the bottom of a tool page. Like ToolFAQ, the about text and video
// show only to logged-out visitors, so they're there for search engines and new
// visitors without crowding the tool for people already using it.

export interface ToolPageVideo {
  url: string;
  title: string;
  description?: string;
  uploadDate?: string;
}

// The page_copy fields a tool page reads. Edited in admin under Page Copy.
export interface ToolPageCopy {
  title: string;
  subtitle: string;
  faq?: { q: string; a: string }[];
  beforeImage?: string;
  afterImage?: string;
  beforeImageAlt?: string;
  afterImageAlt?: string;
  about?: string;
  video?: ToolPageVideo;
}

const SECTION_CLASSES = 'mt-16 pt-12 border-t border-gray-200';

export function getYouTubeVideoId(url: string): string | null {
  const match = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
  return match ? match[1] : null;
}

export function ToolAboutSection({ html }: { html?: string }) {
  const session = useSession();
  if (!html || session) return null;

  return (
    <section className={SECTION_CLASSES}>
      <div
        className="prose prose-gray max-w-3xl prose-h2:text-2xl prose-h2:font-bold prose-h2:text-gray-900 prose-h3:text-lg prose-a:text-primary-600"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
      />
    </section>
  );
}

export function ToolDemoVideo({ video }: { video?: ToolPageVideo }) {
  const session = useSession();
  const [playing, setPlaying] = useState(false);
  const videoId = video?.url ? getYouTubeVideoId(video.url) : null;

  // Video schema can earn a video result in search. Added for every visitor, like the
  // FAQ schema, since it isn't visible.
  useEffect(() => {
    if (!video || !videoId || !video.uploadDate) return;

    const script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('data-tool-video-schema', 'true');
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: video.title,
      description: video.description || video.title,
      thumbnailUrl: `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
      uploadDate: video.uploadDate,
      embedUrl: `https://www.youtube.com/embed/${videoId}`,
      contentUrl: `https://www.youtube.com/watch?v=${videoId}`,
    });
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [video, videoId]);

  if (!video || !videoId || session) return null;

  return (
    <section className={SECTION_CLASSES}>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">{video.title}</h2>
      {video.description && <p className="text-gray-600 mb-6 max-w-3xl">{video.description}</p>}
      <div className="relative w-full max-w-3xl aspect-video rounded-xl overflow-hidden bg-gray-900 shadow-md">
        {playing ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        ) : (
          // Loads YouTube's player only when clicked, so the page stays fast.
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="group absolute inset-0 w-full h-full"
            aria-label={`Play video: ${video.title}`}
          >
            <img
              src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
              alt={video.title}
              loading="lazy"
              width={480}
              height={360}
              className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
            />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="flex items-center justify-center w-16 h-16 rounded-full bg-white/90 shadow-lg group-hover:scale-105 transition-transform">
                <Play className="w-7 h-7 text-gray-900 ml-1" fill="currentColor" />
              </span>
            </span>
          </button>
        )}
      </div>
    </section>
  );
}

// Shown to everyone: it's navigation as much as SEO.
export function RelatedTools({ pageId }: { pageId: string }) {
  const links = getRelatedLinks(pageId);
  if (!links) return null;

  return (
    <section className={SECTION_CLASSES}>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Related tools</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {links.tools.map((tool) => (
          <Link
            key={tool.path}
            to={tool.path}
            className="group flex flex-col p-5 rounded-xl border border-gray-200 bg-white hover:border-primary-300 hover:shadow-md transition-all"
          >
            <span className="flex items-center justify-between font-semibold text-gray-900 group-hover:text-primary-700">
              {tool.name}
              <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-primary-600 group-hover:translate-x-0.5 transition-transform" />
            </span>
            <span className="mt-2 text-sm text-gray-600 leading-relaxed">{tool.blurb}</span>
          </Link>
        ))}
      </div>

      {links.guides.length > 0 && (
        <div className="mt-8">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3">
            <BookOpen className="w-5 h-5 text-gray-500" />
            Guides
          </h3>
          <ul className="space-y-2">
            {links.guides.map((guide) => (
              <li key={guide.path}>
                <Link to={guide.path} className="text-primary-600 hover:text-primary-800 hover:underline">
                  {guide.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
